// What the editor's suggestion popups share: the `/` menu and the `[[` list.
//
// - The popup is mounted by `@tiptap/suggestion` (`props.mount`) at the end of
//   <body> with fixed positioning; it gets the popover layer's z-index. The
//   theme's colors are set on <html>, so a popup outside the app root still
//   follows dark mode.
// - Keys: ↑ ↓ move (wrapping), the confirm keys choose. With no candidates no key
//   is taken: an Enter swallowed there would leave someone who does not know
//   Escape stuck in the menu; passed through, it splits the paragraph and the
//   menu closes as typing would.
// - IME composition is never taken (a confirming Enter must not pick an item).
import type { Editor } from "@tiptap/core";
import { m } from "@trameria/i18n";
import type { Dispatch, SetStateAction } from "react";

/** The panel of a popup: the popover's surface, scrolling inside. */
export const POPUP_PANEL =
  "overflow-y-auto [scrollbar-gutter:auto] rounded-[9px] border border-border bg-surface py-1 text-[13px] shadow-[var(--shadow-popover)]";

/**
 * One row of a menu or list (the text color is the caller's: body or danger).
 * The keyboard position is the hover color, never the accent.
 */
export const POPUP_ROW =
  "flex h-8 w-full flex-none cursor-pointer items-center gap-2 px-3 text-left text-[13px]";

/** The popover layer (same as `Popover`): above the panels, below tooltips. */
export const POPUP_Z = "50";

/**
 * Whether one of the editor's suggestion popups (the `/` menu, the `[[` list)
 * is open. Its Escape is the popup's: a screen that leaves on Escape asks
 * this first.
 */
export function suggestionOpen(editor: Editor): boolean {
  const { state } = editor;
  return state.plugins.some((p) => {
    const s: unknown = p.getState(state);
    return (
      typeof s === "object" &&
      s !== null &&
      "active" in s &&
      (s as { active: unknown }).active === true
    );
  });
}

/** Call before `props.mount(element)`. */
export function raisePopup<T extends HTMLElement>(element: T): T {
  element.style.zIndex = POPUP_Z;
  return element;
}

export function suggestKeyDown<T>(
  event: { key: string },
  {
    items,
    selected,
    setSelected,
    command,
    confirmKeys,
  }: {
    items: readonly T[];
    selected: number;
    setSelected: Dispatch<SetStateAction<number>>;
    command: (item: T) => void;
    /** The `/` menu confirms with Enter only (Tab indents); lists take Tab too. */
    confirmKeys: readonly string[];
  },
): boolean {
  if (items.length === 0) return false;
  if (event.key === "ArrowDown") {
    setSelected((i) => (i + 1) % items.length);
    return true;
  }
  if (event.key === "ArrowUp") {
    setSelected((i) => (i - 1 + items.length) % items.length);
    return true;
  }
  if (confirmKeys.includes(event.key)) {
    const item = items[selected];
    if (item) command(item);
    return true;
  }
  return false;
}

/** The row shown when nothing matches (the `/` menu and the `[[` list say the same). */
export function SuggestEmpty() {
  return (
    <div className="px-3 py-2 text-[12px] text-muted">
      {m.documents_suggest_empty()}
    </div>
  );
}
